import {
	TableRow,
	TableCell,
	Box,
	Button,
	FormControl,
	Autocomplete,
	TextField,
	FormControlLabel,
	Checkbox,
	Typography,
} from '@mui/material'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import React from 'react'
import { WorkArea } from '../../../types/Project/WorkArea'
import { WorkAreaSelectionModal } from './WorkAreaSelectionModal'
import { DirectReportAttendance } from '../../../types/Employee/Attendance'
import { StyledTableCell } from '../../core/StyledTableCell'
import { useProjectFeaturesContext } from '../../project/contexts/ProjectFeaturesContext'
import ConsentDialog from './ConsentDialog'

interface AttendanceRowProps {
	onStatusChange: (status: string) => void
	onLocationChange: (area: WorkArea) => void
	onConsentChange?: () => void
	mapUrl: string | undefined
	attendance: DirectReportAttendance
	workAreas: WorkArea[]
	nameColumn: string
	selectRow: boolean
	handleWorkerSelected?: (worker: DirectReportAttendance, checked: boolean) => void
	selectedWorkers?: number[]
	selectAllCheckBox?: JSX.Element
}

export const AttendanceRow = ({
	onLocationChange,
	onStatusChange,
	onConsentChange,
	mapUrl,
	attendance,
	workAreas,
	nameColumn,
	selectRow,
	handleWorkerSelected,
	selectedWorkers,
	selectAllCheckBox,
}: AttendanceRowProps) => {
	const { isFeatureEnabled } = useProjectFeaturesContext()
	const isDaysOffEnabled = isFeatureEnabled(3)
	const isWorkAreaEnabled = isFeatureEnabled(5)
	const isConsentEnabled = isFeatureEnabled(6)
	return (
		<TableRow
			sx={
				selectRow
					? {
							position: 'sticky',
							bottom: 0,
							backgroundColor: '#f5f5f5',
							borderTop: '2px solid #ccc',
							zIndex: 2,
					  }
					: {}
			}
		>
			<StyledTableCell align='left' sx={{ pr: 0, width: '8rem' }}>
				{!selectRow ? (
					<FormControlLabel
						sx={{ mr: 0 }}
						control={
							<Checkbox
								checked={selectedWorkers && selectedWorkers.includes(attendance.workerId)}
								onChange={(_, checked) =>
									handleWorkerSelected && handleWorkerSelected(attendance, checked)
								}
							/>
						}
						label={''}
					/>
				) : (
					selectAllCheckBox
				)}
			</StyledTableCell>
			<StyledTableCell align='left' sx={{ pr: 0 }}>
				{nameColumn}
			</StyledTableCell>

			<StyledTableCell align='center'>
				<Box
					sx={{
						display: 'inline-block',
						padding: '4px',
						border: attendance.statusError
							? '2px solid red'
							: attendance.status === 'Present'
							? '2px solid #1976d2'
							: '2px solid transparent',
						borderRadius: '8px',
					}}
				>
					<Button
						color='success'
						variant='contained'
						disabled={attendance.status !== null && attendance.status !== 'Present'}
						onClick={() => onStatusChange('Present')}
						size='small'
						startIcon={
							<Checkbox
								checked={attendance.status === 'Present'}
								sx={{
									color: 'white',
									'&.Mui-checked': {
										color: 'white',
									},
									padding: 0,
									margin: 0,
								}}
							/>
						}
					>
						Sign In
					</Button>
				</Box>
			</StyledTableCell>
			<StyledTableCell align='center'>
				<Box
					sx={{
						display: 'inline-block',
						padding: '4px',
						border: attendance.statusError
							? '2px solid red'
							: attendance.status === 'Absent'
							? '2px solid #1976d2'
							: '2px solid transparent',
						borderRadius: '8px',
					}}
				>
					<Button
						color='primary'
						variant='contained'
						disabled={attendance.status !== null && attendance.status !== 'Absent'}
						onClick={() => onStatusChange('Absent')}
						size='small'
						startIcon={
							<Checkbox
								checked={attendance.status === 'Absent'}
								sx={{
									color: 'white',
									'&.Mui-checked': {
										color: 'white',
									},
									padding: 0,
									margin: 0,
								}}
							/>
						}
					>
						Absent
					</Button>
				</Box>
			</StyledTableCell>
			{isDaysOffEnabled && (
				<StyledTableCell align='left'>
					<Box
						sx={{
							display: 'inline-block',
							padding: '4px',
							border: attendance.statusError
								? '2px solid red'
								: attendance.status === 'Days Off'
								? '2px solid #1976d2'
								: '2px solid transparent',
							borderRadius: '8px',
						}}
					>
						<Button
							color='warning'
							variant='contained'
							disabled={attendance.status !== null && attendance.status !== 'Days Off'}
							size='small'
							onClick={() => onStatusChange('Days Off')}
							startIcon={
								<Checkbox
									checked={attendance.status === 'Days Off'}
									sx={{
										color: 'white',
										'&.Mui-checked': {
											color: 'white',
										},
										padding: 0,
										margin: 0,
									}}
								/>
							}
						>
							Days Off
						</Button>
					</Box>
				</StyledTableCell>
			)}
			{isWorkAreaEnabled && (
				<StyledTableCell align='center' sx={{ minWidth: '12rem' }}>
					{(attendance.status === null || attendance.status === 'Present') &&
						(mapUrl ? (
							<WorkAreaSelectionModal
								workAreas={workAreas}
								imageUrl={mapUrl}
								setSelected={onLocationChange}
								selected={attendance.areaName}
								error={attendance.areaError}
							/>
						) : (
							<FormControl fullWidth>
								<Autocomplete
									size={'small'}
									options={workAreas || []}
									getOptionLabel={(wa: WorkArea) => wa.name}
									value={workAreas?.find((wa) => wa.id === attendance.areaId) || null}
									onChange={(_, newValue) => newValue && onLocationChange(newValue)}
									renderInput={(params) => (
										<TextField {...params} label='Choose Location' error={attendance.areaError} />
									)}
									renderOption={(props, option) => {
										const isSuggested = option.id === attendance.suggestedAreaId
										return (
											<li {...props}>
												{option.name}
												{isSuggested && (
													<span
														style={{
															fontSize: '0.8em',
															fontStyle: 'italic',
															marginLeft: '4px',
														}}
													>
														(Suggested)
													</span>
												)}
											</li>
										)
									}}
								/>
							</FormControl>
						))}
				</StyledTableCell>
			)}
			{isConsentEnabled && (
				<StyledTableCell align='center'>
					{attendance.status === null || attendance.status === 'Present' ? (
						!selectRow &&
						(attendance.formSigned ? (
							<AssignmentTurnedInIcon color='success' />
						) : (
							<ConsentDialog
								type='dayStartPrompt'
								workerId={attendance.workerId}
								error={attendance.consentError}
								onConsentChange={onConsentChange}
							/>
						))
					) : (
						<></>
					)}
				</StyledTableCell>
			)}
		</TableRow>
	)
}
